import { Link, useLocation } from "@tanstack/solid-router";
import { type Component, For } from "solid-js";
import { BookOpen, FileText, Menu, Search, Star } from "@fleet/icons";
import { cn } from "@bso/shared";
import { getLastReadBible } from "@/utils/last-read";

interface MobileTabBarProps {
  onOpenMenu: () => void;
}

interface TabItem {
  to: string;
  match: string;
  label: string;
  icon: Component<{ class?: string }>;
  params?: () => Record<string, string>;
}

const itemBase =
  "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 min-h-12 text-3xs font-semibold transition-colors";

/**
 * Primary mobile navigation (hidden on lg+ where the sidebar takes over).
 * Mirrors the reading-focused sections of the legacy mobile app.
 */
export function MobileTabBar(props: MobileTabBarProps) {
  const location = useLocation();

  const tabs: TabItem[] = [
    {
      to: "/bible/$version/$bookAbbr/$chapter",
      match: "/bible",
      label: "Leer",
      icon: BookOpen,
      params: () => {
        const last = getLastReadBible();
        return { version: last.version, bookAbbr: last.bookAbbr, chapter: last.chapter };
      },
    },
    { to: "/search", match: "/search", label: "Buscar", icon: Search },
    { to: "/highlights", match: "/highlights", label: "Destacados", icon: Star },
    { to: "/notes", match: "/notes", label: "Notas", icon: FileText },
  ];

  const isActive = (match: string) => location().pathname.startsWith(match);

  return (
    <nav
      class="lg:hidden flex items-stretch border-t border-border/40 bg-card/85 backdrop-blur-lg shrink-0"
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
      aria-label="Navegación principal"
    >
      <For each={tabs}>
        {(tab) => (
          <Link
            to={tab.to}
            params={tab.params?.()}
            class={cn(
              itemBase,
              isActive(tab.match) ? "text-primary" : "text-muted-foreground hover:text-foreground",
            )}
            aria-current={isActive(tab.match) ? "page" : undefined}
          >
            <tab.icon class="h-5 w-5" />
            <span>{tab.label}</span>
          </Link>
        )}
      </For>

      <button
        type="button"
        onClick={() => {
          props.onOpenMenu();
        }}
        class={cn(itemBase, "text-muted-foreground hover:text-foreground")}
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
        aria-label="Abrir menú"
      >
        <Menu class="h-5 w-5" />
        {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n) */}
        <span>Más</span>
      </button>
    </nav>
  );
}
